define('webshopMenuView',[
  'module',
  'app',
  'backbone',
  'marionette',
  'underscore',
  'modalsAlertView',
  'modalsConfirmationView',
  'webshopCategoriesFinderView',
  'entities/webshop/menu',
  'template!webshopMenuView',
  'template!webshopMenuEditorView',
  'jqueryui'
], function (
  Module,
  App,
  Backbone,
  Marionette,
  _,
  AlertView,
  ConfirmationView,
  CategoriesFinderView,
  MenuApi,
  Tpl,
  editorTpl
) {
  'use strict';

  // Webshop > Menu: editor of the main menu of the webshop (header tabs and mega menu), one per locale
  // (formerly a Google Sheet). The menu is a tree {TopMenu: {Items: [...]}} rendered by the webshop
  // (ai-menu / ai-header):
  //   top (Type 1 mega menu, 2 image grid, 3 link, 4 category link)
  //     1: column > group > link > sub link        2: image tile
  // Items point to webshop categories by Code. The editor works on a copy: every item gets a _uid
  // (dropped on save), changes are kept in an undo stack and published by "Save and publish".

  var TYPE_MEGA = 1, TYPE_IMAGES = 2, TYPE_URL = 3, TYPE_CATEGORY = 4;
  var MAX_UNDO = 100;

  var kinds = function () {
    return {
      top: {
        label: _.i18n('webshop.menu.kind.top', null, 'Tab'),
        add: _.i18n('webshop.menu.add.top', null, 'Add a tab'),
        help: _.i18n('webshop.menu.help.top', null, 'Entry of the menu bar of the site')
      },
      column: {
        label: _.i18n('webshop.menu.kind.column', null, 'Column'),
        add: _.i18n('webshop.menu.add.column', null, 'Add a column'),
        help: _.i18n('webshop.menu.help.column', null, 'Left list of the mega menu; shows its groups on hover')
      },
      tile: {
        label: _.i18n('webshop.menu.kind.tile', null, 'Image'),
        add: _.i18n('webshop.menu.add.tile', null, 'Add an image'),
        help: _.i18n('webshop.menu.help.tile', null, 'Image linking to a category')
      },
      group: {
        label: _.i18n('webshop.menu.kind.group', null, 'Group'),
        add: _.i18n('webshop.menu.add.group', null, 'Add a group'),
        help: _.i18n('webshop.menu.help.group', null, 'Title of a block of links')
      },
      link: {
        label: _.i18n('webshop.menu.kind.link', null, 'Link'),
        add: _.i18n('webshop.menu.add.link', null, 'Add a link'),
        help: _.i18n('webshop.menu.help.link', null, 'Link to a category')
      },
      sublink: {
        label: _.i18n('webshop.menu.kind.sublink', null, 'Sub link'),
        add: _.i18n('webshop.menu.add.sublink', null, 'Add a sub link'),
        help: _.i18n('webshop.menu.help.sublink', null, 'Link shown under a link')
      }
    };
  };

  var types = function () {
    return [
      {value: TYPE_MEGA, icon: 'fa-columns', label: _.i18n('webshop.menu.type.mega', null, 'Mega menu'),
        help: _.i18n('webshop.menu.type.megaHelp', null, 'Columns, groups and links')},
      {value: TYPE_IMAGES, icon: 'fa-th-large', label: _.i18n('webshop.menu.type.images', null, 'Image grid'),
        help: _.i18n('webshop.menu.type.imagesHelp', null, 'Images linking to categories')},
      {value: TYPE_CATEGORY, icon: 'fa-folder-o', label: _.i18n('webshop.menu.type.category', null, 'Category link'),
        help: _.i18n('webshop.menu.type.categoryHelp', null, 'Direct link to a category')},
      {value: TYPE_URL, icon: 'fa-link', label: _.i18n('webshop.menu.type.url', null, 'Page link'),
        help: _.i18n('webshop.menu.type.urlHelp', null, 'Direct link to any page')}
    ];
  };

  // Kind of the children of a node (null: no children)
  var childKind = function (kind, node) {
    switch (kind) {
      case 'top':
        if (node.Type === TYPE_MEGA)
          return 'column';
        if (node.Type === TYPE_IMAGES)
          return 'tile';
        return null;
      case 'column':
        return 'group';
      case 'group':
        return 'link';
      case 'link':
        return 'sublink';
      default:
        return null;
    }
  };

  var newNode = function (kind) {
    switch (kind) {
      case 'top':
        return {Name: _.i18n('webshop.menu.new.top', null, 'New tab'), Code: '', Url: '', Type: TYPE_MEGA, Items: []};
      case 'column':
        return {Name: _.i18n('webshop.menu.new.column', null, 'New column'), Code: '', ImageUrl: '', Text: '', Items: []};
      case 'tile':
        return {Name: '', Code: '', ImageUrl: '', Text: '', Items: []};
      case 'group':
        return {Name: _.i18n('webshop.menu.new.group', null, 'New group'), Code: '', Items: []};
      case 'link':
        return {Name: '', Code: '', Tag: '', Items: []};
      default:
        return {Name: '', Code: ''};
    }
  };

  // Copy without the editor's fields (_uid...)
  var strip = function (items) {
    return _.map(items, function (item) {
      var copy = {};
      _.each(item, function (value, key) {
        if (key.charAt(0) === '_')
          return;
        copy[key] = key === 'Items' ? strip(value || []) : value;
      });
      return copy;
    });
  };

  var formatDate = function (value) {
    if (!value)
      return '';
    // 'YYYY-MM-DD HH:mm:ss' -> 'DD/MM/YYYY HH:mm'
    var m = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/.exec(value);
    return m ? m[3] + '/' + m[2] + '/' + m[1] + ' ' + m[4] + ':' + m[5] : value;
  };

  var sourceLabel = function (source) {
    if (source === 'sheet')
      return _.i18n('webshop.menu.source.sheet', null, 'Google Sheet (former)');
    if (source === 'dashboard')
      return _.i18n('webshop.menu.source.dashboard', null, 'Dashboard');
    return source || '';
  };

  Module.exports = Marionette.ItemView.extend({
    template: Tpl,
    className: 'webshop-menu-view',

    ui: {
      locale: 'select.mm-locale',
      version: '.mm-version',
      dirty: '.mm-dirty',
      message: '.mm-message',
      problems: '.mm-problems',
      undo: '.mm-undo',
      redo: '.mm-redo',
      save: '.mm-save',
      moreMenu: '.mm-more-menu',
      importFile: '.mm-import-file',
      search: 'input.mm-search',
      tree: '.mm-tree',
      editor: '.mm-editor',
      preview: '.mm-preview'
    },

    events: {
      'change @ui.locale': 'onLocaleChange',
      'click @ui.problems': 'onNextProblem',
      'click @ui.undo': 'undo',
      'click @ui.redo': 'redo',
      'click @ui.save': 'onSave',
      'click .mm-history': 'onHistory',
      'click .mm-more-toggle': 'onMoreToggle',
      'click .mm-more-item': 'onMoreItem',
      'change @ui.importFile': 'onImportFile',
      'input @ui.search': 'onSearch',
      'click .mm-expand-all': 'onExpandAll',
      'click .mm-collapse-all': 'onCollapseAll',
      'click .mm-add-top': 'onAddTop',

      'click .mm-tree .mm-row': 'onRowClick',
      'click .mm-tree .mm-caret': 'onCaretClick',
      'click .mm-tree .mm-row-action': 'onRowAction',

      'focus .mm-editor [data-field]': 'onFieldFocus',
      'input .mm-editor [data-field]': 'onFieldInput',
      'change .mm-editor input[name="mm-type"]': 'onTypeChange',
      'click .mm-editor .mm-find-category': 'onFindCategory',
      'click .mm-editor .mm-choose-image': 'onChooseImage',
      'click .mm-editor .mm-clear-image': 'onClearImage',
      'click .mm-editor [data-action]': 'onEditorAction',
      'click .mm-editor .mm-crumb': 'onCrumbClick',

      'click .mm-preview [data-uid]': 'onPreviewClick'
    },

    initialize: function () {
      this.locale = this.options.locale || '';
      this.locales = [];
      this.items = [];
      this.baseId = null;
      this.baseInfo = null;
      this.savedJson = '[]';
      this.undoStack = [];
      this.redoStack = [];
      this.expanded = {};
      this.selectedUid = null;
      this.previewColumnUid = null;
      this.query = '';
      this.uidSeq = 0;
      this.index = {};
      this.categories = {};      // code -> category | false (unknown), for this.locale
      this.pendingCodes = {};
      this.images = null;        // path -> url

      this.renderLater = _.debounce(_.bind(this.renderTreeAndPreview, this), 120);
      this.resolveLater = _.debounce(_.bind(this.resolveCategories, this), 400);

      this.onKeyDownBound = _.bind(this.onKeyDown, this);
      this.onBeforeUnloadBound = _.bind(this.onBeforeUnload, this);
      this.onDocumentClickBound = _.bind(function (e) {
        if (!$(e.target).closest('.mm-more').length)
          this.ui.moreMenu.hide();
      }, this);
    },

    onShow: function () {
      $(document).on('keydown', this.onKeyDownBound);
      $(document).on('click', this.onDocumentClickBound);
      $(window).on('beforeunload', this.onBeforeUnloadBound);

      this.renderEditor();
      this.updateToolbar();

      MenuApi.locales().done(_.bind(function (locales) {
        if (this.isDestroyed)
          return;
        this.locales = locales;
        if (!this.locale || !_.findWhere(locales, {locale: this.locale}))
          this.locale = (_.findWhere(locales, {locale: 'fr'}) || locales[0] || {locale: ''}).locale;
        this.renderLocales();
        this.load();
      }, this)).fail(_.bind(this.showError, this));

      MenuApi.images().done(_.bind(function (images) {
        this.setImages(images);
      }, this));
    },

    onDestroy: function () {
      $(document).off('keydown', this.onKeyDownBound);
      $(document).off('click', this.onDocumentClickBound);
      $(window).off('beforeunload', this.onBeforeUnloadBound);
    },

    showError: function (message) {
      AlertView.prototype.open('', message);
    },

    flash: function (text) {
      this.ui.message.text(text).stop(true, true).show();
      clearTimeout(this.flashTimeout);
      this.flashTimeout = setTimeout(_.bind(function () {
        if (!this.isDestroyed)
          this.ui.message.fadeOut();
      }, this), 4000);
    },

    //----------------------------------------
    // Loading

    renderLocales: function () {
      this.ui.locale.empty();
      _.each(this.locales, function (l) {
        this.ui.locale.append($('<option>').val(l.locale).text(l.locale.toUpperCase()));
      }, this);
      this.ui.locale.val(this.locale);
    },

    load: function () {
      if (!this.locale)
        return;

      App.navigate('webshop/menu/' + this.locale, {trigger: false, replace: true});

      MenuApi.get(this.locale).done(_.bind(function (result) {
        if (this.isDestroyed)
          return;
        this.categories = {};
        this.setMenu(result.menu.TopMenu.Items, true);
        this.setBase(result);
        this.undoStack = [];
        this.redoStack = [];
        this.selectedUid = null;
        this.expanded = {};
        this.renderAll();
        this.resolveCategories();
      }, this)).fail(_.bind(this.showError, this));
    },

    setBase: function (result) {
      this.baseId = result.id;
      this.baseInfo = result;
      this.savedJson = JSON.stringify(strip(this.items));
    },

    // items: plain menu items (from the api, an import...); assigns the _uids
    setMenu: function (items, keepUids) {
      var assign = _.bind(function (list) {
        _.each(list, function (item) {
          if (!keepUids || !item._uid)
            item._uid = 'n' + (++this.uidSeq);
          if (item.Type !== undefined && item.Type !== null && item.Type !== '')
            item.Type = parseInt(item.Type, 10);
          if (item.Items)
            assign(item.Items);
        }, this);
      }, this);

      this.items = items || [];
      assign(this.items);
      this.buildIndex();
    },

    // uid -> {node, parent, siblings, level, kind, top}
    buildIndex: function () {
      this.index = {};
      var walk = _.bind(function (list, parent, level, kind, top) {
        _.each(list, function (node) {
          var entry = {node: node, parent: parent, siblings: list, level: level, kind: kind, top: top || node};
          this.index[node._uid] = entry;
          var ck = childKind(kind, node);
          if (ck && node.Items)
            walk(node.Items, node, level + 1, ck, entry.top);
        }, this);
      }, this);
      walk(this.items, null, 0, 'top', null);
    },

    isDirty: function () {
      return JSON.stringify(strip(this.items)) !== this.savedJson;
    },

    //----------------------------------------
    // Categories / images

    allCodes: function () {
      var codes = [];
      _.each(this.index, function (entry) {
        if (entry.node.Code)
          codes.push(entry.node.Code);
      });
      return _.uniq(codes);
    },

    resolveCategories: function () {
      var locale = this.locale;
      var codes = _.filter(this.allCodes(), function (code) {
        return this.categories[code] === undefined && !this.pendingCodes[code];
      }, this);

      if (!codes.length)
        return;

      _.each(codes, function (code) {
        this.pendingCodes[code] = true;
      }, this);

      MenuApi.categories(locale, codes).done(_.bind(function (result) {
        if (this.isDestroyed || locale !== this.locale)
          return;
        _.each(codes, function (code) {
          delete this.pendingCodes[code];
          this.categories[code] = result[code] || false;
        }, this);
        this.renderTreeAndPreview();
        this.renderCategoryInfo();
      }, this)).fail(_.bind(function () {
        _.each(codes, function (code) {
          delete this.pendingCodes[code];
        }, this);
      }, this));
    },

    setImages: function (images) {
      this.images = {};
      this.imageList = images;
      _.each(images, function (image) {
        this.images[image.path] = image.url;
      }, this);
      if (!this.isDestroyed) {
        this.renderPreview();
        this.renderEditor();
      }
    },

    imageSrc: function (path) {
      if (!path || !this.images)
        return '';
      return this.images[path] || this.images[decodeURIComponent(path)] || '';
    },

    // Name shown on the site: the item's name, else the name of its category
    displayName: function (node) {
      if (node.Name)
        return node.Name;
      var category = node.Code ? this.categories[node.Code] : null;
      if (category)
        return category.name || category.label || node.Code;
      return '';
    },

    //----------------------------------------
    // Problems

    problemsOf: function (entry) {
      var node = entry.node;
      var problems = [];
      var category = node.Code ? this.categories[node.Code] : undefined;
      var codeRequired = this.codeRequired(entry);

      if (codeRequired && !node.Code)
        problems.push({level: 'error', message: _.i18n('webshop.menu.problem.noCode', null,
          'No category: this item is not shown on the site')});
      if (node.Code && category === false)
        problems.push({level: 'error', message: _.i18n('webshop.menu.problem.unknownCode', null,
          'The category "%s" does not exist').replace('%s', node.Code)});
      if (node.Code && category && category.status <= 0)
        problems.push({level: 'warning', message: _.i18n('webshop.menu.problem.inactive', null,
          'The category "%s" is not active').replace('%s', node.Code)});
      if (entry.kind === 'top' && node.Type === TYPE_URL && !node.Url)
        problems.push({level: 'error', message: _.i18n('webshop.menu.problem.noUrl', null, 'No link')});
      if (entry.kind === 'tile' && !node.ImageUrl)
        problems.push({level: 'error', message: _.i18n('webshop.menu.problem.noImage', null,
          'No image: this item is not shown on the site')});
      if (!this.displayName(node) && !(node.Code && category === undefined))
        problems.push({level: 'error', message: _.i18n('webshop.menu.problem.noName', null, 'No name')});

      return problems;
    },

    allProblems: function () {
      var result = [];
      var walk = _.bind(function (list) {
        _.each(list, function (node) {
          var entry = this.index[node._uid];
          if (!entry)
            return;
          var problems = this.problemsOf(entry);
          if (problems.length)
            result.push({uid: node._uid, problems: problems});
          if (childKind(entry.kind, node))
            walk(node.Items || []);
        }, this);
      }, this);
      walk(this.items);
      return result;
    },

    codeRequired: function (entry) {
      return _.contains(['tile', 'link', 'sublink'], entry.kind) || (entry.kind === 'top' && entry.node.Type === TYPE_CATEGORY);
    },

    //----------------------------------------
    // Rendering

    renderAll: function () {
      this.renderTreeAndPreview();
      this.renderEditor();
    },

    renderTreeAndPreview: function () {
      if (this.isDestroyed)
        return;
      this.buildIndex();
      this.renderTree();
      this.renderPreview();
      this.updateToolbar();
    },

    updateToolbar: function () {
      var info = this.baseInfo;
      if (info && info.id) {
        this.ui.version.html(_.i18n('webshop.menu.version', null, 'Version %s').replace('%s', '#' + _.escape(info.id)) +
          ' · ' + _.escape(formatDate(info.updatedAt)) + ' · ' + _.escape(sourceLabel(info.source)));
      } else if (info) {
        this.ui.version.text(_.i18n('webshop.menu.noVersion', null, 'No menu yet for this language'));
      }

      var dirty = this.isDirty();
      this.ui.dirty.toggle(dirty);
      this.ui.save.toggleClass('mm-attention', dirty);
      this.ui.undo.prop('disabled', !this.undoStack.length);
      this.ui.redo.prop('disabled', !this.redoStack.length);

      var problems = this.allProblems();
      var errors = _.filter(problems, function (p) {
        return _.findWhere(p.problems, {level: 'error'});
      }).length;
      this.ui.problems.toggle(problems.length > 0).toggleClass('has-errors', errors > 0);
      this.ui.problems.find('.text').text(problems.length === 1 ?
        _.i18n('webshop.menu.oneProblem', null, '1 problem') :
        _.i18n('webshop.menu.problems', null, '%s problems').replace('%s', problems.length));
    },

    // Visible nodes for the search: matching nodes and their ancestors
    searchMatches: function () {
      if (!this.query)
        return null;
      var q = this.query.toLowerCase();
      var visible = {};
      _.each(this.index, function (entry, uid) {
        var node = entry.node;
        var text = [node.Name, node.Code, node.Tag, node.Url, this.displayName(node)].join(' ').toLowerCase();
        if (text.indexOf(q) < 0)
          return;
        visible[uid] = 'match';
        var parent = entry.parent;
        while (parent) {
          if (!visible[parent._uid])
            visible[parent._uid] = 'ancestor';
          parent = this.index[parent._uid].parent;
        }
      }, this);
      return visible;
    },

    renderTree: function () {
      var scroll = this.ui.tree.scrollTop();
      var visible = this.searchMatches();
      var k = kinds();

      var renderNodes = _.bind(function (list, kind, parent, level) {
        var html = '<div class="mm-children" data-kind="' + kind + '" data-parent="' + (parent ? parent._uid : '') + '">';

        _.each(list, function (node) {
          if (visible && !visible[node._uid])
            return;

          var entry = this.index[node._uid];
          var ck = childKind(kind, node);
          var children = ck ? (node.Items || []) : [];
          var open = ck && (visible ? true : !!this.expanded[node._uid]);
          var problems = this.problemsOf(entry);
          var hasError = !!_.findWhere(problems, {level: 'error'});
          var name = this.displayName(node);
          var typeLabel = kind === 'top' ? (_.findWhere(types(), {value: node.Type}) || {}).label : k[kind].label;
          var category = node.Code ? this.categories[node.Code] : undefined;

          html += '<div class="mm-node" data-uid="' + node._uid + '">';
          html += '<div class="mm-row' + (node._uid === this.selectedUid ? ' selected' : '') +
            (visible && visible[node._uid] === 'match' ? ' match' : '') + '" style="padding-left:' + (6 + level * 18) + 'px">';
          html += '<span class="mm-handle"><i class="fa fa-ellipsis-v"></i><i class="fa fa-ellipsis-v"></i></span>';
          html += '<span class="mm-caret">' + (ck ? '<i class="fa fa-caret-' + (open ? 'down' : 'right') + '"></i>' : '') + '</span>';
          html += '<span class="mm-kind kind-' + kind + '">' + _.escape(typeLabel || '') + '</span>';
          html += '<span class="mm-name' + (node.Name ? '' : ' inherited') + '">' +
            (name ? _.escape(name) : '<em>' + _.escape(_.i18n('webshop.menu.noName', null, '(no name)')) + '</em>') + '</span>';
          if (node.Tag)
            html += '<span class="mm-tag">' + _.escape(node.Tag) + '</span>';
          if (node.Code)
            html += '<span class="mm-code' + (category === false ? ' unknown' : '') +
              (category && category.status <= 0 ? ' inactive' : '') + '">' + _.escape(node.Code) + '</span>';
          if (kind === 'top' && node.Type === TYPE_URL && node.Url)
            html += '<span class="mm-code">' + _.escape(node.Url) + '</span>';
          if (ck)
            html += '<span class="mm-count">' + children.length + '</span>';
          if (problems.length)
            html += '<span class="mm-problem-icon ' + (hasError ? 'error' : 'warning') + '" title="' +
              _.escape(_.pluck(problems, 'message').join('\n')) + '"><i class="fa fa-exclamation-triangle"></i></span>';
          html += '<span class="mm-row-actions">';
          if (ck)
            html += '<span class="mm-row-action" data-action="add-child" title="' + _.escape(k[ck].add) + '"><i class="fa fa-plus"></i></span>';
          html += '<span class="mm-row-action" data-action="duplicate" title="' + _.escape(_.i18n('webshop.menu.duplicate', null, 'Duplicate')) + '"><i class="fa fa-clone"></i></span>';
          html += '<span class="mm-row-action danger" data-action="delete" title="' + _.escape(_.i18n('common.delete', null, 'Delete')) + '"><i class="fa fa-trash-o"></i></span>';
          html += '</span>';
          html += '</div>';

          if (open)
            html += renderNodes(children, ck, node, level + 1);

          html += '</div>';
        }, this);

        if (!list.length && parent)
          html += '<div class="mm-empty" style="padding-left:' + (30 + level * 18) + 'px">' +
            _.escape(_.i18n('webshop.menu.empty', null, 'Empty: add items or drop them here')) + '</div>';

        html += '</div>';
        return html;
      }, this);

      if (!this.items.length) {
        this.ui.tree.html('<div class="mm-tree-empty">' + _.escape(_.i18n('webshop.menu.emptyMenu', null,
          'This menu is empty. Add a tab, or copy the menu of another language (More).')) + '</div>');
        return;
      }

      this.ui.tree.html(renderNodes(this.items, 'top', null, 0));
      this.ui.tree.scrollTop(scroll);

      if (!this.query)
        this.initSortable();
    },

    // Drag & drop: an item can move inside its list or to another list of the same kind
    initSortable: function () {
      var view = this;
      this.ui.tree.find('.mm-children').each(function () {
        var kind = $(this).data('kind');
        $(this).sortable({
          items: '> .mm-node',
          handle: '> .mm-row > .mm-handle',
          connectWith: '.mm-tree .mm-children[data-kind="' + kind + '"]',
          placeholder: 'mm-placeholder',
          tolerance: 'pointer',
          axis: 'y',
          start: function (e, ui) {
            ui.placeholder.height(ui.item.children('.mm-row').outerHeight());
          },
          stop: function () {
            view.onSorted(kind);
          }
        });
      });
    },

    onSorted: function (kind) {
      var lists = [];
      this.ui.tree.find('.mm-children[data-kind="' + kind + '"]').each(_.bind(function (i, el) {
        var parentUid = $(el).data('parent');
        var uids = $(el).children('.mm-node').map(function () {
          return $(this).data('uid');
        }).get();
        lists.push({parent: parentUid ? this.index[parentUid].node : null, uids: uids});
      }, this));

      var changed = _.some(lists, function (list) {
        var current = list.parent ? (list.parent.Items || []) : this.items;
        return _.pluck(current, '_uid').join(',') !== list.uids.join(',');
      }, this);
      if (!changed)
        return;

      this.pushUndo();
      _.each(lists, function (list) {
        var nodes = _.map(list.uids, function (uid) {
          return this.index[uid].node;
        }, this);
        if (list.parent)
          list.parent.Items = nodes;
        else
          this.items = nodes;
      }, this);
      this.changed(true);
    },

    renderEditor: function () {
      if (this.isDestroyed)
        return;
      var entry = this.selectedUid ? this.index[this.selectedUid] : null;
      if (!entry) {
        this.ui.editor.html(editorTpl({node: null}));
        return;
      }

      var node = entry.node;
      var kind = entry.kind;
      var k = kinds();
      var ck = childKind(kind, node);
      var category = node.Code ? this.categories[node.Code] : null;
      var isTop = kind === 'top';
      var index = _.indexOf(entry.siblings, node);

      var breadcrumb = [];
      var current = entry;
      while (current) {
        breadcrumb.unshift({uid: current.node._uid, label: this.displayName(current.node) || k[current.kind].label});
        current = current.parent ? this.index[current.parent._uid] : null;
      }

      var codeHelp = '';
      if (kind === 'column')
        codeHelp = _.i18n('webshop.menu.hint.columnCode', null, 'Optional: the column title and the button under its groups link to this category');
      else if (kind === 'group')
        codeHelp = _.i18n('webshop.menu.hint.groupCode', null, 'Optional: the group title links to this category');

      this.ui.editor.html(editorTpl({
        node: node,
        kind: kind,
        kindLabel: k[kind].label,
        kindHelp: k[kind].help,
        breadcrumb: breadcrumb,
        types: types(),
        nameRequired: !node.Code,
        namePlaceholder: category ? (category.name || category.label) : '',
        showCode: !isTop || node.Type === TYPE_CATEGORY,
        codeRequired: this.codeRequired(entry),
        codeHelp: codeHelp,
        showUrl: isTop && (node.Type === TYPE_URL || node.Type === TYPE_MEGA),
        urlRequired: isTop && node.Type === TYPE_URL,
        urlHelp: node.Type === TYPE_URL ?
          _.i18n('webshop.menu.hint.url', null, 'Path of the page on the site (/demande-de-devis-gratuit/) or full address') :
          _.i18n('webshop.menu.hint.mobileUrl', null, 'Optional: link of the tab title on mobile'),
        showTag: kind === 'link',
        tags: this.allTags(),
        showImage: kind === 'column' || kind === 'tile',
        imageRequired: kind === 'tile',
        imageSrc: this.imageSrc(node.ImageUrl),
        imageHelp: kind === 'column' ?
          _.i18n('webshop.menu.hint.columnImage', null, 'Optional: shown on the right of the mega menu') :
          _.i18n('webshop.menu.hint.tileImage', null, 'Image of the tile'),
        problems: this.problemsOf(entry),
        childLabel: ck ? k[ck].add : '',
        siblingLabel: k[kind].add,
        canMoveUp: index > 0,
        canMoveDown: index < entry.siblings.length - 1
      }));

      this.renderCategoryInfo();
    },

    renderCategoryInfo: function () {
      var entry = this.selectedUid ? this.index[this.selectedUid] : null;
      var $info = this.ui.editor.find('.mm-category-info');
      if (!entry || !$info.length)
        return;

      var code = entry.node.Code;
      var category = code ? this.categories[code] : null;
      var html = '';

      if (!code)
        html = '';
      else if (category === undefined)
        html = '<span class="pending"><i class="fa fa-spinner fa-spin"></i></span>';
      else if (category === false)
        html = '<span class="unknown"><i class="fa fa-times-circle"></i> ' +
          _.escape(_.i18n('webshop.menu.unknownCategory', null, 'Unknown category')) + '</span>';
      else
        html = '<span class="' + (category.status > 0 ? 'ok' : 'inactive') + '"><i class="fa ' +
          (category.status > 0 ? 'fa-check-circle' : 'fa-pause-circle') + '"></i> ' +
          _.escape(category.name || category.label) +
          (category.status > 0 ? '' : ' (' + _.escape(_.i18n('webshop.menu.inactive', null, 'inactive')) + ')') +
          '</span> <a href="#webshop/categories/' + encodeURIComponent(category.id) + '" target="_blank" title="' +
          _.escape(_.i18n('webshop.menu.openCategory', null, 'Open the category')) + '"><i class="fa fa-external-link"></i></a>';

      $info.html(html);

      var $name = this.ui.editor.find('[data-field="Name"]');
      $name.attr('placeholder', category ? (category.name || category.label) : '');
    },

    allTags: function () {
      var tags = [];
      _.each(this.index, function (entry) {
        if (entry.node.Tag)
          tags.push(entry.node.Tag);
      });
      return _.uniq(tags).sort();
    },

    // Approximation of the menu of the site (desktop): menu bar, then the panel of the selected tab
    renderPreview: function () {
      if (this.isDestroyed)
        return;
      var e = _.escape;
      var selected = this.selectedUid ? this.index[this.selectedUid] : null;
      var top = selected ? selected.top : this.items[0];
      var html = '<div class="pv-bar">';

      _.each(this.items, function (item) {
        html += '<div class="pv-tab' + (top && item._uid === top._uid ? ' active' : '') + '" data-uid="' + item._uid + '">' +
          e(this.displayName(item) || '…') + (childKind('top', item) ? ' <i class="fa fa-angle-down"></i>' : '') + '</div>';
      }, this);
      html += '</div>';

      if (!top) {
        this.ui.preview.html(html);
        return;
      }

      var cls = _.bind(function (node) {
        var problems = this.problemsOf(this.index[node._uid] || {node: node});
        return (node._uid === this.selectedUid ? ' pv-selected' : '') +
          (_.findWhere(problems, {level: 'error'}) ? ' pv-hidden' : '');
      }, this);

      html += '<div class="pv-panel">';

      if (top.Type === TYPE_MEGA) {
        var columns = top.Items || [];
        // Column shown: the one of the selection, else the last one clicked, else the first one
        var column = null;
        if (selected && selected.level >= 1) {
          var entry = selected;
          while (entry.level > 1)
            entry = this.index[entry.parent._uid];
          column = entry.node;
        }
        if (!column)
          column = _.find(columns, function (c) {
            return c._uid === this.previewColumnUid;
          }, this) || columns[0];

        html += '<div class="pv-mega"><div class="pv-columns">';
        _.each(columns, function (c) {
          html += '<div class="pv-column' + (column && c._uid === column._uid ? ' active' : '') + cls(c) + '" data-uid="' + c._uid + '">' +
            e(this.displayName(c) || '…') + '<i class="fa fa-angle-right"></i></div>';
        }, this);
        html += '</div>';

        if (column) {
          html += '<div class="pv-content"><div class="pv-groups">';
          _.each(column.Items || [], function (g) {
            html += '<div class="pv-group">';
            html += '<div class="pv-group-title' + (g.Code ? ' linked' : '') + cls(g) + '" data-uid="' + g._uid + '">' + e(this.displayName(g) || '…') + '</div>';
            _.each(g.Items || [], function (l) {
              html += '<div class="pv-link' + cls(l) + '" data-uid="' + l._uid + '">' + e(this.displayName(l) || l.Code || '…') +
                (l.Tag ? '<span class="pv-tag">' + e(l.Tag) + '</span>' : '') + '</div>';
              _.each(l.Items || [], function (s) {
                html += '<div class="pv-sublink' + cls(s) + '" data-uid="' + s._uid + '">' + e(this.displayName(s) || s.Code || '…') + '</div>';
              }, this);
            }, this);
            html += '</div>';
          }, this);
          if (!(column.Items || []).length)
            html += '<div class="pv-empty">' + e(_.i18n('webshop.menu.preview.emptyColumn', null, 'No group in this column')) + '</div>';
          html += '</div>';
          if (column.Code)
            html += '<div class="pv-footer"><span class="pv-button" data-uid="' + column._uid + '">' + e(this.displayName(column)) + '</span></div>';
          html += '</div>';
          if (column.ImageUrl) {
            var src = this.imageSrc(column.ImageUrl);
            html += '<div class="pv-image" data-uid="' + column._uid + '">' +
              (src ? '<img src="' + e(src) + '"/>' : '<i class="fa fa-picture-o"></i>') + '</div>';
          }
        }
        html += '</div>';
      } else if (top.Type === TYPE_IMAGES) {
        html += '<div class="pv-tiles">';
        _.each(top.Items || [], function (t) {
          var src = this.imageSrc(t.ImageUrl);
          html += '<div class="pv-tile' + cls(t) + '" data-uid="' + t._uid + '"><div class="pv-tile-image">' +
            (src ? '<img src="' + e(src) + '"/>' : '<i class="fa fa-picture-o"></i>') + '</div><div class="pv-tile-name">' +
            e(this.displayName(t) || '…') + '</div></div>';
        }, this);
        if (!(top.Items || []).length)
          html += '<div class="pv-empty">' + e(_.i18n('webshop.menu.preview.emptyTiles', null, 'No image yet')) + '</div>';
        html += '</div>';
      } else {
        var target = top.Type === TYPE_URL ? top.Url : top.Code;
        html += '<div class="pv-direct" data-uid="' + top._uid + '"><i class="fa fa-link"></i> ' +
          e(_.i18n('webshop.menu.preview.direct', null, 'Direct link, no panel')) +
          (target ? ' → <span class="mm-code">' + e(target) + '</span>' : '') + '</div>';
      }

      html += '</div>';
      this.ui.preview.html(html);
    },

    //----------------------------------------
    // Changes / undo

    pushUndo: function () {
      this.undoStack.push(JSON.stringify(this.items));
      if (this.undoStack.length > MAX_UNDO)
        this.undoStack.shift();
      this.redoStack = [];
    },

    // structural: the editor is rendered again (else only the tree / preview, to keep the focus)
    changed: function (structural) {
      this.buildIndex();
      if (structural) {
        this.renderTreeAndPreview();
        this.renderEditor();
      } else {
        this.renderLater();
      }
    },

    undo: function () {
      if (!this.undoStack.length)
        return;
      this.redoStack.push(JSON.stringify(this.items));
      this.restore(this.undoStack.pop());
    },

    redo: function () {
      if (!this.redoStack.length)
        return;
      this.undoStack.push(JSON.stringify(this.items));
      this.restore(this.redoStack.pop());
    },

    restore: function (json) {
      this.items = JSON.parse(json);
      this.buildIndex();
      if (this.selectedUid && !this.index[this.selectedUid])
        this.selectedUid = null;
      this.renderTreeAndPreview();
      this.renderEditor();
    },

    //----------------------------------------
    // Selection / tree

    select: function (uid, focusTree) {
      this.selectedUid = uid;
      var entry = this.index[uid];
      if (entry) {
        // The ancestors are opened so that the item is visible
        var parent = entry.parent;
        while (parent) {
          this.expanded[parent._uid] = true;
          parent = this.index[parent._uid].parent;
        }
        if (entry.level === 1 && entry.top.Type === TYPE_MEGA)
          this.previewColumnUid = uid;
      }
      this.renderTreeAndPreview();
      this.renderEditor();

      if (focusTree !== false) {
        var $row = this.ui.tree.find('.mm-node[data-uid="' + uid + '"] > .mm-row');
        if ($row.length) {
          var top = $row.position().top;
          if (top < 0 || top > this.ui.tree.height() - 30)
            this.ui.tree.scrollTop(this.ui.tree.scrollTop() + top - this.ui.tree.height() / 3);
        }
      }
    },

    onRowClick: function (e) {
      var uid = $(e.currentTarget).closest('.mm-node').data('uid');
      if (uid !== this.selectedUid)
        this.select(uid, false);
    },

    onCaretClick: function (e) {
      e.stopPropagation();
      var uid = $(e.currentTarget).closest('.mm-node').data('uid');
      this.expanded[uid] = !this.expanded[uid];
      this.renderTree();
    },

    onRowAction: function (e) {
      e.stopPropagation();
      var uid = $(e.currentTarget).closest('.mm-node').data('uid');
      this.doAction($(e.currentTarget).data('action'), uid);
    },

    onEditorAction: function (e) {
      if ($(e.currentTarget).prop('disabled'))
        return;
      this.doAction($(e.currentTarget).data('action'), this.selectedUid);
    },

    onCrumbClick: function (e) {
      this.select($(e.currentTarget).data('uid'));
    },

    onPreviewClick: function (e) {
      e.stopPropagation();
      this.select($(e.currentTarget).data('uid'));
    },

    onSearch: function () {
      this.query = $.trim(this.ui.search.val());
      this.renderTree();
    },

    onExpandAll: function () {
      _.each(this.index, function (entry, uid) {
        // The links (thousands) stay closed
        if (entry.level < 2)
          this.expanded[uid] = true;
      }, this);
      this.renderTree();
    },

    onCollapseAll: function () {
      this.expanded = {};
      this.renderTree();
    },

    onAddTop: function () {
      this.addNode(null, 'top', this.items.length);
    },

    addNode: function (parent, kind, position) {
      this.pushUndo();
      var node = newNode(kind);
      node._uid = 'n' + (++this.uidSeq);
      var list;
      if (parent) {
        parent.Items = parent.Items || [];
        list = parent.Items;
        this.expanded[parent._uid] = true;
      } else {
        list = this.items;
      }
      list.splice(position, 0, node);
      this.buildIndex();
      this.select(node._uid);

      // Links / images are made of a category: its field first
      var field = node.Name === '' ? 'Code' : 'Name';
      var $input = this.ui.editor.find('[data-field="' + field + '"]');
      $input.focus();
      if (field === 'Name')
        $input.select();
    },

    doAction: function (action, uid) {
      var entry = this.index[uid];
      if (!entry)
        return;
      var node = entry.node;
      var index = _.indexOf(entry.siblings, node);

      switch (action) {
        case 'add-child':
          var ck = childKind(entry.kind, node);
          if (ck)
            this.addNode(node, ck, (node.Items || []).length);
          break;
        case 'add-sibling':
          this.addNode(entry.parent, entry.kind, index + 1);
          break;
        case 'up':
        case 'down':
          var to = action === 'up' ? index - 1 : index + 1;
          if (to < 0 || to >= entry.siblings.length)
            return;
          this.pushUndo();
          entry.siblings.splice(index, 1);
          entry.siblings.splice(to, 0, node);
          this.changed(true);
          break;
        case 'duplicate':
          this.pushUndo();
          var copy = JSON.parse(JSON.stringify(strip([node])))[0];
          this.setUids(copy);
          entry.siblings.splice(index + 1, 0, copy);
          this.buildIndex();
          this.select(copy._uid);
          break;
        case 'delete':
          var count = this.countDescendants(node);
          var remove = _.bind(function () {
            this.pushUndo();
            entry.siblings.splice(index, 1);
            if (this.selectedUid === uid || !this.index[this.selectedUid]) {
              var next = entry.siblings[index] || entry.siblings[index - 1] || entry.parent;
              this.selectedUid = next ? next._uid : null;
            }
            this.changed(true);
            this.flash(_.i18n('webshop.menu.deleted', null, 'Deleted. Ctrl+Z to undo.'));
          }, this);

          if (count > 0)
            ConfirmationView.prototype.open({
              text: _.i18n('webshop.menu.deleteConfirm', null, 'Delete "%s" and its %d sub-items?')
                .replace('%s', this.displayName(node) || '').replace('%d', count),
              onConfirm: remove
            });
          else
            remove();
          break;
      }
    },

    setUids: function (node) {
      node._uid = 'n' + (++this.uidSeq);
      _.each(node.Items || [], this.setUids, this);
    },

    countDescendants: function (node) {
      return _.reduce(node.Items || [], function (sum, child) {
        return sum + 1 + this.countDescendants(child);
      }, 0, this);
    },

    //----------------------------------------
    // Editor fields

    onFieldFocus: function () {
      // One undo step per field edition
      this.fieldSnapshotPending = true;
    },

    onFieldInput: function (e) {
      var entry = this.index[this.selectedUid];
      if (!entry)
        return;
      var field = $(e.currentTarget).data('field');
      var value = $(e.currentTarget).val();

      if (this.fieldSnapshotPending) {
        this.pushUndo();
        this.fieldSnapshotPending = false;
      }

      entry.node[field] = value;

      if (field === 'Code') {
        value = $.trim(value);
        entry.node.Code = value;
        this.renderCategoryInfo();
        if (value && this.categories[value] === undefined)
          this.resolveLater();
      }
      if (field === 'ImageUrl')
        this.updateImageThumb();

      this.changed(false);
    },

    updateImageThumb: function () {
      var entry = this.index[this.selectedUid];
      var src = entry ? this.imageSrc(entry.node.ImageUrl) : '';
      this.ui.editor.find('.mm-image-thumb').html(src ? $('<img>').attr('src', src) : '<i class="fa fa-picture-o"></i>');
    },

    setField: function (field, value) {
      var entry = this.index[this.selectedUid];
      if (!entry)
        return;
      this.pushUndo();
      entry.node[field] = value;
      if (field === 'Code' && value && this.categories[value] === undefined)
        this.resolveCategories();
      this.changed(true);
    },

    onTypeChange: function (e) {
      var entry = this.index[this.selectedUid];
      if (!entry)
        return;
      var node = entry.node;
      var type = parseInt($(e.currentTarget).val(), 10);
      var previousKind = childKind('top', node);
      var nextKind = childKind('top', {Type: type});

      var apply = _.bind(function (clearChildren) {
        this.pushUndo();
        node.Type = type;
        if (clearChildren)
          node.Items = [];
        this.changed(true);
      }, this);

      // The sub items of another kind (columns <-> images) would not be shown
      if ((node.Items || []).length && previousKind !== nextKind) {
        ConfirmationView.prototype.open({
          text: _.i18n('webshop.menu.typeConfirm', null, 'With this type, the %d sub-items of this tab are removed. Continue?')
            .replace('%d', node.Items.length),
          onConfirm: function () {
            apply(true);
          }
        });
        // The radio shows the current type until the confirmation
        this.renderEditor();
      } else {
        apply(false);
      }
    },

    onFindCategory: function () {
      CategoriesFinderView.prototype.open({
        title: _.i18n('webshop.menu.findCategory', null, 'Find a category'),
        onSelected: _.bind(function (categories) {
          if (categories.length > 0)
            this.setField('Code', categories[0].get('code'));
        }, this)
      });
    },

    onChooseImage: function () {
      var entry = this.index[this.selectedUid];
      if (!entry)
        return;
      require(['windows', 'modalView', 'webshopMenuImagesView'], _.bind(function (Windows, ModalView, ImagesView) {
        var model = new Backbone.Model({path: entry.node.ImageUrl || ''});
        var View = ModalView.extend({childView: ImagesView});
        var view = new View({
          model: model,
          images: this.imageList || [],
          onUploaded: _.bind(function (image) {
            this.setImages(_.reject(this.imageList || [], function (i) {
              return i.path === image.path;
            }).concat([image]));
          }, this),
          confirmButtonText: _.i18n('webshop.menu.chooseImage', null, 'Choose'),
          onConfirm: _.bind(function (model) {
            if (model.get('path') !== entry.node.ImageUrl)
              this.setField('ImageUrl', model.get('path'));
          }, this)
        });
        Windows.open({
          view: view,
          modal: true,
          width: 900,
          height: 620,
          title: _.i18n('webshop.menu.chooseImageTitle', null, 'Choose an image')
        });
      }, this));
    },

    onClearImage: function () {
      this.setField('ImageUrl', '');
    },

    //----------------------------------------
    // Toolbar

    onLocaleChange: function () {
      var locale = this.ui.locale.val();
      var change = _.bind(function () {
        this.locale = locale;
        this.load();
      }, this);

      if (this.isDirty()) {
        this.ui.locale.val(this.locale);
        ConfirmationView.prototype.open({
          text: _.i18n('webshop.menu.leaveConfirm', null, 'The changes of this menu are not saved. Discard them?'),
          onConfirm: change
        });
      } else {
        change();
      }
    },

    onNextProblem: function () {
      var problems = this.allProblems();
      if (!problems.length)
        return;
      var uids = _.pluck(problems, 'uid');
      var i = _.indexOf(uids, this.selectedUid);
      this.select(uids[(i + 1) % uids.length]);
    },

    onSave: function () {
      if (!this.locale)
        return;
      var errors = _.filter(this.allProblems(), function (p) {
        return _.findWhere(p.problems, {level: 'error'});
      }).length;

      if (errors > 0)
        ConfirmationView.prototype.open({
          text: _.i18n('webshop.menu.saveWithProblems', null,
            '%d items have problems: they are hidden or broken on the site. Publish anyway?').replace('%d', errors),
          onConfirm: _.bind(function () {
            this.save(false);
          }, this)
        });
      else
        this.save(false);
    },

    save: function (force) {
      var menu = {TopMenu: {Items: strip(this.items)}};
      this.ui.save.prop('disabled', true);

      MenuApi.save(this.locale, menu, this.baseId, force).done(_.bind(function (result) {
        if (this.isDestroyed)
          return;
        this.setBase(result);
        this.updateToolbar();
        this.flash(_.i18n('webshop.menu.published', null, 'Menu published on the site'));
        var locale = _.findWhere(this.locales, {locale: this.locale});
        if (locale)
          locale.versions++;
      }, this)).fail(_.bind(function (message, json) {
        if (json && json.conflict) {
          ConfirmationView.prototype.open({
            text: _.i18n('webshop.menu.conflict', null,
              'Another version of this menu was published on %s (%source) since you opened it. Replace it with yours?')
              .replace('%source', sourceLabel(json.source)).replace('%s', formatDate(json.updatedAt)),
            onConfirm: _.bind(function () {
              this.save(true);
            }, this)
          });
        } else {
          this.showError(message);
        }
      }, this)).always(_.bind(function () {
        if (!this.isDestroyed)
          this.ui.save.prop('disabled', false);
      }, this));
    },

    // Puts a menu (version, import, copy) in the editor, as unsaved changes
    loadItems: function (items, message) {
      this.pushUndo();
      this.setMenu(JSON.parse(JSON.stringify(strip(items))), false);
      this.selectedUid = null;
      this.renderAll();
      this.resolveCategories();
      this.flash(message);
    },

    onHistory: function () {
      require(['windows', 'modalView', 'webshopMenuHistoryView'], _.bind(function (Windows, ModalView, HistoryView) {
        var View = ModalView.extend({childView: HistoryView});
        var view = new View({
          model: new Backbone.Model({id: null}),
          locale: this.locale,
          currentId: this.baseId,
          sourceLabel: sourceLabel,
          formatDate: formatDate,
          confirmButtonText: _.i18n('webshop.menu.history.load', null, 'Load in the editor'),
          onConfirm: _.bind(function (model) {
            if (!model.get('id'))
              return;
            MenuApi.version(model.get('id')).done(_.bind(function (result) {
              if (!this.isDestroyed)
                this.loadItems(result.menu.TopMenu.Items, _.i18n('webshop.menu.versionLoaded', null,
                  'Version #%s loaded: "Save and publish" to restore it').replace('%s', result.id));
            }, this)).fail(_.bind(this.showError, this));
          }, this)
        });
        Windows.open({
          view: view,
          modal: true,
          width: 760,
          height: 560,
          title: _.i18n('webshop.menu.historyTitle', null, 'History of the menu') + ' (' + this.locale.toUpperCase() + ')'
        });
      }, this));
    },

    onMoreToggle: function () {
      this.ui.moreMenu.toggle();
    },

    onMoreItem: function (e) {
      this.ui.moreMenu.hide();
      switch ($(e.currentTarget).data('action')) {
        case 'copy':
          this.onCopy();
          break;
        case 'export':
          this.onExport();
          break;
        case 'import':
          this.ui.importFile.val('').click();
          break;
        case 'revert':
          // Back to the published version (undoable)
          if (this.isDirty()) {
            this.pushUndo();
            this.setMenu(JSON.parse(this.savedJson), false);
            this.selectedUid = null;
            this.renderAll();
          }
          break;
      }
    },

    onCopy: function () {
      require(['windows', 'modalView', 'webshopMenuLocaleView'], _.bind(function (Windows, ModalView, LocaleView) {
        var others = _.filter(this.locales, function (l) {
          return l.locale !== this.locale;
        }, this);
        var View = ModalView.extend({childView: LocaleView});
        var view = new View({
          model: new Backbone.Model({locale: others.length ? others[0].locale : ''}),
          locales: others,
          confirmButtonText: _.i18n('webshop.menu.copy', null, 'Copy'),
          onConfirm: _.bind(function (model) {
            var from = model.get('locale');
            if (!from)
              return;
            MenuApi.get(from).done(_.bind(function (result) {
              if (!this.isDestroyed)
                this.loadItems(result.menu.TopMenu.Items, _.i18n('webshop.menu.copied', null,
                  'Menu %s copied: translate the names, then "Save and publish"').replace('%s', from.toUpperCase()));
            }, this)).fail(_.bind(this.showError, this));
          }, this)
        });
        Windows.open({
          view: view,
          modal: true,
          width: 480,
          height: 260,
          title: _.i18n('webshop.menu.copyFrom', null, 'Copy the menu of another language')
        });
      }, this));
    },

    onExport: function () {
      var json = JSON.stringify({TopMenu: {Items: strip(this.items)}}, null, 2);
      var blob = new Blob([json], {type: 'application/json'});
      var a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = 'menu-' + this.locale + '.json';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(function () {
        URL.revokeObjectURL(a.href);
      }, 1000);
    },

    onImportFile: function () {
      var file = this.ui.importFile[0].files[0];
      if (!file)
        return;
      var reader = new FileReader();
      reader.onload = _.bind(function () {
        var data;
        try {
          data = JSON.parse(reader.result);
        } catch (err) {
          this.showError(_.i18n('webshop.menu.importInvalid', null, 'This file is not a menu (invalid JSON)'));
          return;
        }
        var items = _.isArray(data) ? data : (data && data.TopMenu && data.TopMenu.Items);
        if (!_.isArray(items)) {
          this.showError(_.i18n('webshop.menu.importInvalid', null, 'This file is not a menu (invalid JSON)'));
          return;
        }
        this.loadItems(items, _.i18n('webshop.menu.imported', null, 'Menu imported: "Save and publish" to put it online'));
      }, this);
      reader.readAsText(file);
    },

    onKeyDown: function (e) {
      if (!(e.ctrlKey || e.metaKey) || !this.$el.is(':visible'))
        return;
      var key = String.fromCharCode(e.which).toLowerCase();
      var inField = $(e.target).is('input, textarea, select');

      if (key === 's') {
        e.preventDefault();
        this.onSave();
      } else if (!inField && key === 'z') {
        e.preventDefault();
        if (e.shiftKey)
          this.redo();
        else
          this.undo();
      } else if (!inField && key === 'y') {
        e.preventDefault();
        this.redo();
      }
    },

    onBeforeUnload: function () {
      if (this.isDirty())
        return _.i18n('webshop.menu.leaveConfirm', null, 'The changes of this menu are not saved. Discard them?');
    }
  });
});

